import type { PersonStanding } from './contracts';
import { PAYMENT_WINDOW_HOURS } from './eTransfer';
import type { Level } from './levels';
import type { RegistrationTier } from './teamRegistration';
import type { LevelPlay, Tournament } from './tournaments';

/** What staff review: one tournament, or the league's registration season. */
export const REVIEW_KINDS = ['tournament', 'league'] as const;
export type ReviewKind = (typeof REVIEW_KINDS)[number];

export const REVIEW_STATUSES = [
  'Pending',
  'Approved',
  'Waitlisted',
  'Rejected',
] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

/** Decisions staff make; a team becomes Pending only when its players submit it. */
export const DECISIONS = ['Approved', 'Waitlisted', 'Rejected'] as const;
export type Decision = (typeof DECISIONS)[number];

export interface LeagueHistoryEntry {
  /** Such as `Fall 2026`. */
  term: string;
  flavour: string;
  team: string;
  level: Level;
  /** YYYY-MM-DD. */
  startedOn: string;
}

export interface ClinicHistoryEntry {
  /** Such as `Fall 2026`. */
  term: string;
  /** The clinic name without its term, such as `INT Burnaby Thu`. */
  name: string;
  /** Blank for a clinic with no level, such as `FF`. */
  level: Level | '';
  /** YYYY-MM-DD. */
  startedOn: string;
}

export interface ReviewPlayer {
  memberId: string;
  name: string;
  standing: PersonStanding;
  /** Current staff positions, such as `Clinics Coordinator`. */
  positions: string[];
  captain: boolean;
  waiverSigned: boolean;
  /** What sets the player's level for this tournament; null for the league. */
  levelPlay: LevelPlay | null;
  /** Newest first. */
  league: LeagueHistoryEntry[];
  /** Newest first. */
  clinics: ClinicHistoryEntry[];
  /** Other active teams the player is on in the same tournament or season. */
  otherTeams: string[];
}

export interface ReviewTeam {
  id: string;
  name: string;
  /** The league flavour the team registered for; null for a tournament team. */
  flavour: string | null;
  status: ReviewStatus;
  /** ISO time of the first submission. */
  submittedAt: string | null;
  tier: RegistrationTier | null;
  rejectionReason: string;
  /** ISO time staff sent the approval email; null until they send it. */
  approvalEmailedAt: string | null;
  feePaid: boolean;
  players: ReviewPlayer[];
}

export interface TeamReview {
  kind: ReviewKind;
  /** The tournament's name, or the registration season's, such as `Winter 2027`. */
  title: string;
  detail: string;
  /** The level the roster must fit; null for the league. */
  tournamentLevel: Tournament['level'] | null;
  teams: ReviewTeam[];
}

export interface ApprovalEmailPreview {
  from: string;
  to: string;
  captain: string;
  subject: string;
  html: string;
  text: string;
}

export const REVIEW_COPY = {
  title: 'Team review',
  homeDescription: 'Approve, waitlist, or reject league and tournament teams',
  loading: 'Loading teams…',
  loadError: 'We couldn’t load the teams. Try again.',
  noTeams: 'No registration season or upcoming tournament.',
  empty: 'No teams here yet.',
  approvedCount: (approved: number) => `${approved} approved`,
  queueSummary: (pending: number, waitlisted: number) =>
    `${pending} pending · ${waitlisted} waitlisted`,
  openRosters: 'Show all rosters',
  showRoster: 'Show roster',
  hideRoster: 'Hide roster',
  submitted: (when: string) => `Submitted ${when}`,
  rejectedBecause: (reason: string) => `Rejected: ${reason}`,
  approve: 'Approve',
  waitlist: 'Waitlist',
  reject: 'Reject',
  rejectReason: 'Reason for rejecting',
  rejectReasonHint: 'The team’s players see this in the portal.',
  rejectTeam: 'Reject team',
  cancel: 'Cancel',
  emailNotSent: 'Approval email not sent yet',
  emailSent: (when: string) => `Approval email sent ${when}`,
  previewEmail: 'Preview email',
  sendEmail: 'Send email',
  selectTeam: (team: string) => `Select ${team}`,
  selectedCount: (n: number) => `${n} ${n === 1 ? 'team' : 'teams'} selected`,
  sendEmails: (n: number) => `Email ${n} approved`,
  clearSelection: 'Clear selection',
  emailsSentToast: (n: number) =>
    `${n} approval ${n === 1 ? 'email' : 'emails'} sent.`,
  emailsFailed: (n: number) =>
    `${n} ${n === 1 ? 'email didn’t' : 'emails didn’t'} send. Try again.`,
  emailTitle: (captain: string) => `Approval email to ${captain}`,
  deadlineNote: `The ${PAYMENT_WINDOW_HOURS}-hour payment window starts when you send this email.`,
  send: (captain: string) => `Send to ${captain}`,
  emailLoadError: 'We couldn’t load the email preview. Try again.',
  emailSentToast: (captain: string) => `Approval email sent to ${captain}.`,
  emailFailed: 'The email didn’t send. Try again.',
  decisionFailed: 'We couldn’t save that decision. Try again.',
  invalidDecision:
    'Choose approve, waitlist, or reject. A rejection needs a reason.',
  notInReview: 'This team is no longer in review. Reload to see its status.',
  notApproved: 'Approve this team before you email the captain.',
  alreadyEmailed: 'This team was already emailed. Reload to see when.',
  decidedNotice: (
    staff: string,
    decision: Decision,
    team: string,
    /** The tournament, or the flavour and season, such as `Matcha in Winter 2027`. */
    where: string,
  ) => `${staff} ${decision.toLowerCase()} ${team} for ${where}.`,
  emailedNotice: (staff: string, captain: string, team: string) =>
    `${staff} sent ${captain} the approval email for ${team}.`,
  player: 'Player',
  leagueHistory: 'League history',
  clinicHistory: 'Clinic history',
  captain: 'Captain',
  waiverUnsigned: 'Waiver unsigned',
  noHistory: 'New to FTLO',
  more: (count: number) => `+${count} more`,
  less: 'Show less',
  tournamentLevel: 'Tournament level',
  setsLevel: 'Sets the tournament level',
  unsignedWaivers: (count: number) =>
    `${count} unsigned ${count === 1 ? 'waiver' : 'waivers'}`,
  levelIssue: (player: string, issue: 'above' | 'below', level: string) =>
    `${player} plays ${issue} ${level}`,
  alsoOn: (player: string, team: string) => `${player} is also on ${team}`,
  standing: { member: 'FTLO', staff: 'Staff', public: 'Public' },
} as const;
